import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppConfigService } from '../AppConfigService';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;

  constructor(private appConfigService: AppConfigService) {
    this.initialize();
  }

  private initialize(): void {
    try {
      // Subscribe to config to get ApplicationInsightsConnectionString
      this.appConfigService.getConfig().pipe(take(1)).subscribe(
        (appConfig) => {
          const connectionString = appConfig?.ApplicationInsightsConnectionString;
          
          if (connectionString) {
            this.appInsights = new ApplicationInsights({
              config: {
                connectionString: connectionString,
                enableAutoRouteTracking: true,
                disableFetchTracking: false,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                enableAjaxPerfTracking: true
              }
            });
            
            this.appInsights.loadAppInsights();
            console.log('Application Insights initialized');
          } else {
            console.warn('Application Insights connection string not provided');
          }
        },
        (error) => {
          console.error('Failed to get config for Application Insights:', error);
        }
      );
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  trackEvent(name: string, properties?: { [key: string]: any }): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name }, properties);
    }
  }

  trackException(exception: Error, severityLevel?: number, properties?: { [key: string]: any }): void {
    if (this.appInsights) {
      this.appInsights.trackException({ 
        exception, 
        severityLevel: severityLevel || 3, // Default to Error level
        properties 
      });
    }
  }

  trackPageView(name?: string, uri?: string, properties?: { [key: string]: any }): void {
    if (this.appInsights) {
      this.appInsights.trackPageView({ 
        name: name || window.document.title, 
        uri: uri || window.location.href,
        properties 
      });
    }
  }

  setUserId(userId: string): void {
    if (this.appInsights) {
      this.appInsights.setAuthenticatedUserContext(userId);
    }
  }

  clearUserId(): void {
    if (this.appInsights) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  flush(): void {
    if (this.appInsights) {
      this.appInsights.flush();
    }
  }
}